import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { copy } from '../../config/copy';
import { api } from '../../lib/api';
import { AlbumList } from '../albums/AlbumList';
import { PostList } from '../posts/PostList';
import { TrackList } from '../tracks/TrackList';
import { FollowCards } from './FollowCards';
import { Rewinds } from './Rewinds';
import type { Profile } from './types';

/**
 * The sections of a profile, in the order their owner put them.
 *
 * ## Two columns, each an ordered list
 *
 * A single ordering with the columns derived from it is the tidier model and
 * the first one to reach for, because a phone shows one column and can then
 * never disagree with a desktop. It does not survive these particular sections:
 * tracks and albums are tall, the follow cards are two rows of faces, and
 * splitting five items down the middle puts three tall things on the left and
 * two short ones on the right - a worse layout than the fixed one it replaces.
 *
 * So a column each. A narrow screen reads the left list and then the right,
 * which is exactly what the fixed layout already did - updates has always
 * appeared below the music on a phone. This makes the arrangement editable
 * without changing what narrow screens do.
 *
 * ## Only the owner can drag, and only the owner sees that they can
 *
 * A visitor gets the same sections with no handles and no drag targets. The
 * arrangement is a thing about the profile, not a thing about reading it.
 */
type Widget = 'tracks' | 'albums' | 'rewinds' | 'updates' | 'followers' | 'following';
type Column = 'left' | 'right';

export interface WidgetLayout {
  left: Widget[];
  right: Widget[];
}

interface Drag {
  widget: Widget;
  from: Column;
}

export function ProfileLayout({
  profile,
  isOwner,
}: {
  profile: Profile;
  isOwner: boolean;
}) {
  const queryClient = useQueryClient();

  /*
   * The arrangement being dragged, which is the server's until somebody moves
   * something. Held locally so a drop repaints immediately rather than after a
   * round trip - a section that hangs where it was for 300ms reads as a drop
   * that missed.
   */
  const [local, setLocal] = useState<WidgetLayout | null>(null);
  const [drag, setDrag] = useState<Drag | null>(null);

  const layout = local ?? profile.widget_layout;

  const save = useMutation({
    mutationFn: (widget_layout: WidgetLayout) => api.patch('/me', { widget_layout }),
    /*
     * The local copy is kept, not cleared.
     *
     * Clearing it once the refetch resolved looked right and was a race:
     * `invalidateQueries` resolving does not mean this component has re-rendered
     * with the new profile, so for a moment `layout` fell back to the *previous*
     * one - and a second drag started in that moment computed its move from a
     * stale arrangement and silently undid the first. It reproduced about one
     * run in three.
     *
     * So whoever is dragging keeps their own copy for as long as they are on the
     * page, and the server's normalised version is picked up on the next load.
     * The client only ever sends arrangements it built from the server's own
     * list, so the two can only differ if a section was retired mid-session.
     */
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['profile'] }),
    // Left where it was dropped rather than snapped back: the drag worked, the
    // request did not, and the next load will tell the truth.
  });

  /** Move the dragged section to sit before `target`, or to the end of `column`. */
  const drop = (column: Column, target: Widget | null) => {
    if (drag === null) return;

    const next: WidgetLayout = {
      left: layout.left.filter((w) => w !== drag.widget),
      right: layout.right.filter((w) => w !== drag.widget),
    };

    const into = next[column];
    const at = target === null ? into.length : into.indexOf(target);

    into.splice(at < 0 ? into.length : at, 0, drag.widget);

    setDrag(null);

    // Nothing moved - a drag that ended where it started is not a change worth
    // a request, and saving one would repaint the page for no reason.
    if (JSON.stringify(next) === JSON.stringify(layout)) return;

    setLocal(next);
    save.mutate(next);
  };

  const column = (name: Column) => (
    <div
      /*
       * `min-w-0` is what keeps a phone's layout on the phone.
       *
       * A grid item's automatic minimum size is its content, not zero, so this
       * column refused to go narrower than the widest thing in it. The widest
       * thing is a track title, and `truncate` sets `white-space: nowrap` -
       * which means the title's *full* width counts toward that minimum even
       * though truncation would have handled it. The column came out 478px
       * inside a 342px cell, stretched the document to 502, and took the
       * header's primary button off the right-hand edge of the screen with it.
       */
      className="flex min-w-0 flex-col gap-10"
      data-profile-column={name}
      onDragOver={isOwner ? (event) => event.preventDefault() : undefined}
      /*
       * A drop on the column itself, rather than on a section in it, appends.
       * Without this the bottom of a column is dead space and the only way to
       * move something last is to aim at the gap under the final section.
       */
      onDrop={isOwner ? () => drop(name, null) : undefined}
    >
      {layout[name].map((widget) => (
        <Section
          key={widget}
          widget={widget}
          profile={profile}
          isOwner={isOwner}
          dragging={drag?.widget === widget}
          onDragStart={() => setDrag({ widget, from: name })}
          onDragEnd={() => setDrag(null)}
          onDrop={() => drop(name, widget)}
        />
      ))}
    </div>
  );

  return (
    <div className="mt-8 grid items-start gap-10 lg:grid-cols-2">
      {column('left')}
      {column('right')}
    </div>
  );
}

function Section({
  widget,
  profile,
  isOwner,
  dragging,
  onDragStart,
  onDragEnd,
  onDrop,
}: {
  widget: Widget;
  profile: Profile;
  isOwner: boolean;
  dragging: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDrop: () => void;
}) {
  const body = render(widget, profile, isOwner);

  /*
   * A hidden section is not a gap you can drop into.
   *
   * The follow cards can be switched off and `PostList` draws nothing when
   * updates are unavailable, so a section can legitimately render to nothing -
   * and an empty drop target with a handle floating in it is a control that
   * appears to do something and does not.
   */
  if (body === null) return null;

  if (!isOwner) return <div data-widget={widget}>{body}</div>;

  return (
    <div
      draggable
      data-widget={widget}
      data-dragging={dragging ? 'true' : undefined}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        // Or the column's own handler fires too and appends it instead.
        event.stopPropagation();
        onDrop();
      }}
      className={`group relative rounded-[var(--radius-card)] transition-opacity ${
        dragging ? 'opacity-40' : ''
      }`}
    >
      {/*
        The handle appears on hover, and only for the owner.

        Permanently visible grips on your own profile would put five pieces of
        editing furniture on the page you are trying to look at, and this is
        also the page people come to read.
      */}
      <span
        aria-hidden
        data-drag-handle={widget}
        className="absolute -left-5 top-1 hidden cursor-grab select-none text-faint opacity-0 transition-opacity group-hover:opacity-100 lg:block"
        title="Drag to rearrange"
      >
        ⠿
      </span>

      {body}
    </div>
  );
}

/** What each name actually draws. Null when the section has nothing to show. */
function render(widget: Widget, profile: Profile, isOwner: boolean): ReactNode {
  switch (widget) {
    case 'tracks':
      return (
        <section aria-labelledby="profile-tracks">
          <h2 id="profile-tracks" className="border-b border-line pb-3 text-lg font-bold tracking-tight">
            {copy['noun.track.plural']}
          </h2>

          <TrackList username={profile.username} isOwner={isOwner} />
        </section>
      );

    case 'albums':
      return (
        <section aria-labelledby="profile-albums">
          <h2 id="profile-albums" className="border-b border-line pb-3 text-lg font-bold tracking-tight">
            {copy['noun.collection.plural']}
          </h2>

          {/* Still three a row, but reached at a larger window than the
              full-width grid needs - see `compact`. */}
          <AlbumList username={profile.username} isOwner={isOwner} compact />
        </section>
      );

    case 'rewinds':
      /*
       * `Rewinds` draws nothing when there is nothing rewound, and `Section`
       * drops a widget whose body is null - so a profile that has never passed
       * anything on gets no heading over an empty list.
       */
      return <Rewinds username={profile.username} />;

    case 'updates':
      /*
       * `PostList` decides whether it draws anything: the endpoint is behind a
       * flag and answers 404 when it is off, which is also what a signed-out
       * reader gets for a profile with nothing to show. The flag is deliberately
       * not re-checked here - it is not on the session for a signed-out reader,
       * and gating on it hid public updates from exactly the people they are
       * public for.
       */
      return <PostList username={profile.username} isOwner={isOwner} />;

    case 'followers':
      return profile.show_followers ? <FollowCards profile={profile} only="followers" /> : null;

    case 'following':
      return profile.show_following ? <FollowCards profile={profile} only="following" /> : null;
  }
}
